import moment from 'moment-hijri';

export const HIJRI_MONTHS = [
  'محرم',
  'صفر',
  'ربيع الأول',
  'ربيع الثاني',
  'جمادى الأولى',
  'جمادى الآخرة',
  'رجب',
  'شعبان',
  'رمضان',
  'شوال',
  'ذو القعدة',
  'ذو الحجة',
];

export type CalendarMode = 'gregorian' | 'hijri';

export interface HijriDateParts {
  year: number;
  month: number;
  day: number;
}

/** تواريخ محفوظة كـ YYYY-MM-DD لكن السنة هجرية (مثل 1447-12-18) */
const HIJRI_STORED_YEAR_MIN = 1300;
const HIJRI_STORED_YEAR_MAX = 1500;

export const isHijriYearStoredAsIso = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = parseInt(value.slice(0, 4), 10);
  return year >= HIJRI_STORED_YEAR_MIN && year <= HIJRI_STORED_YEAR_MAX;
};

export const isValidGregorianValue = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  if (isHijriYearStoredAsIso(value)) return false;
  return moment(value, 'YYYY-MM-DD', true).isValid();
};

/** تحليل تاريخ محفوظ (ميلادي أو هجري بصيغة ISO) */
export const parseStoredDateValue = (value: string): moment.Moment | null => {
  const s = value?.trim();
  if (!s) return null;

  if (isHijriYearStoredAsIso(s)) {
    const [y, mo, d] = s.split('-').map((n) => parseInt(n, 10));
    const m = moment(`${y}/${mo}/${d}`, 'iYYYY/iM/iD', true);
    return m.isValid() ? m : null;
  }

  if (isValidGregorianValue(s)) {
    return moment(s, 'YYYY-MM-DD');
  }

  return null;
};

export const hijriLabelFromMoment = (m: moment.Moment): string => {
  const day = m.iDate();
  const month = m.iMonth() + 1;
  const year = m.iYear();
  if (
    Number.isNaN(day) ||
    Number.isNaN(month) ||
    Number.isNaN(year) ||
    month < 1 ||
    month > 12
  ) {
    return '';
  }
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return `${year}/${monthStr}/${dayStr} هـ`;
};

export const gregorianToHijriParts = (value: string): HijriDateParts | null => {
  const m = parseStoredDateValue(value);
  if (!m?.isValid()) return null;
  const day = m.iDate();
  const month = m.iMonth() + 1;
  const year = m.iYear();
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;
  return { year, month, day };
};

/** YYYY-MM-DD ميلادي بأرقام لاتينية — للمقارنة والتخزين */
export const formatGregorianDateOnly = (m: moment.Moment): string =>
  m.clone().locale('en').format('YYYY-MM-DD');

export const hijriPartsToGregorian = (parts: HijriDateParts): string | null => {
  const m = moment(
    `${parts.year}/${parts.month}/${parts.day}`,
    'iYYYY/iM/iD',
    true
  );
  if (!m.isValid()) return null;
  return formatGregorianDateOnly(m);
};

export const getHijriDaysInMonth = (year: number, month: number): number =>
  moment.iDaysInMonth(year, month - 1);

export const formatHijriLabel = (value: string): string => {
  const m = parseStoredDateValue(value);
  if (!m?.isValid()) return '';
  return hijriLabelFromMoment(m);
};

export const formatGregorianLabel = (value: string): string => {
  const m = parseStoredDateValue(value);
  if (!m?.isValid()) return '';
  return m.format('DD/MM/YYYY') + ' م';
};

/** عرض الحقل الميلادي: يوم/شهر/سنة */
export const formatGregorianFieldValue = (value: string): string => {
  if (!isValidGregorianValue(value)) return '';
  return moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
};

export const getDefaultHijriParts = (): HijriDateParts => {
  const m = moment();
  return {
    year: m.iYear(),
    month: m.iMonth() + 1,
    day: m.iDate(),
  };
};

export const isGregorianInRange = (
  value: string,
  min?: string,
  max?: string
): boolean => {
  if (!isValidGregorianValue(value)) return false;
  if (min && isValidGregorianValue(min) && value < min) return false;
  if (max && isValidGregorianValue(max) && value > max) return false;
  return true;
};

/** مفتاح ترتيب حسب التقويم الهجري (سنة → شهر → يوم) */
export const hijriSortKeyFromGregorian = (value: string): number => {
  const m = parseStoredDateValue(value);
  if (!m?.isValid()) return Number.MAX_SAFE_INTEGER;
  const month = m.iMonth() + 1;
  const day = m.iDate();
  const year = m.iYear();
  if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return year * 10000 + month * 100 + day;
};

/** مقارنة تاريخين ميلاديين حسب الترتيب في التقويم الهجري */
export const compareGregorianByHijriCalendar = (a: string, b: string): number =>
  hijriSortKeyFromGregorian(a) - hijriSortKeyFromGregorian(b);
